import {
  HarnessAppVersionContext,
  HarnessTurnContext,
  HarnessWriteTarget,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  HarnessInterrupt,
  HarnessInterruptType,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import { asString, isRecord } from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * What one call on a conversation-owned harness carries (doc 37 N1): the project or workspace its
 * headers name, the version and branch its body repeats, and where the maker is.
 */
export type HarnessCallScope = {
  /** Null from an organization page, which names no workspace. */
  workspaceId: string | null
  /** Null from a page outside any project. */
  appId: string | null
  /** `app_version_id` and `branch`, as every execution, abandon and pause body repeats them. Null
   * from a page outside any project, which has no version. */
  body: { app_version_id: string; branch: string } | null
  /** The turn's `context{…}`: where the maker is, which never moves the target. */
  context: HarnessTurnContext
}

/** The gates whose answer acts on the plan, which makes their `/resume` a plan call (doc 37 N1).
 * Answering any other — `ask_user`, `read_grant` — is a turn. */
const PLAN_GATES: ReadonlySet<HarnessInterruptType> = new Set<HarnessInterruptType>([
  'brief_review',
  'retarget_review',
  'plan_review',
  'direct_review',
  'specialist_review',
  'merge_conflict',
])

export function isPlanGate(type: HarnessInterruptType | null | undefined): boolean {
  return !!type && PLAN_GATES.has(type)
}

/**
 * A turn's scope: the page the maker is on, for its headers, its body and its context alike
 * (doc 37 N1). The relay authorizes the page — a project's AI permission on a project page, the
 * workspace's or the organization's off one — and the harness reads any other project as the
 * maker, so a turn never carries the target's headers.
 *
 * While a project page has no version to report yet it falls back to the target's head, then to
 * where the maker last spoke: those are the two the harness already knows.
 */
export function turnScopeFor(
  target: HarnessWriteTarget | null,
  route: HarnessTurnContext | null,
  lastContext: HarnessTurnContext | null
): HarnessCallScope | null {
  const where = pageOf(target, route, lastContext)

  return where && { ...addressOf(where), context: where }
}

/**
 * A plan call's scope: answering a review of the plan, running or previewing it, amending it,
 * abandoning it (doc 37 N1).
 *
 * **Headers** are the write target's once there is one: the relay's `AIAGENT_EDIT` on them is the
 * permission to change it, and the harness refuses any other (`409 target_headers_required`).
 * Before a target they are the open proposal's — the project the first change lands in, wherever
 * the maker is now — and without one the page's, as on a turn.
 *
 * **The body** names the target's head once there is one: the harness acts on the target whatever
 * is sent, and a body that agrees with it cannot be misread. Before one, the version the proposal
 * was read at.
 *
 * **The context** is the page, as on a turn — except where the page names no version and the
 * headers name a project: the harness needs that project's version with its headers, so the
 * context is the project the call acts on.
 */
export function planScopeFor(
  target: HarnessWriteTarget | null,
  route: HarnessTurnContext | null,
  lastContext: HarnessTurnContext | null,
  proposed: HarnessAppVersionContext | null = null
): HarnessCallScope | null {
  const where = pageOf(target, route, lastContext)

  if (!where) {
    return null
  }

  const actsOn = target ? targetContext(target) : (proposed ?? where)
  const context = where.scope !== 'app_version' && actsOn.scope === 'app_version' ? actsOn : where

  return { ...addressOf(actsOn), context }
}

/**
 * Where an open plan gate says its first change lands: the gate's `write_target` read at the
 * version the proposal was grounded on — a plan's `grounded_at`, a brief's `app_version_id`. The
 * harness names it on every proposal before the conversation has a target; a proposal made
 * outside any project names no project, and so none is returned.
 */
export function proposedTarget(
  interrupt: HarnessInterrupt | null
): HarnessAppVersionContext | null {
  if (!interrupt || !isPlanGate(interrupt.type) || !isRecord(interrupt.payload)) {
    return null
  }

  const proposal = interrupt.payload
  const where = isRecord(proposal['write_target']) ? proposal['write_target'] : {}
  const workspaceId = asString(where['workspace_id'])
  const appId = asString(where['app_id'])
  const proposalVersionId =
    asString(proposal['grounded_at']) || asString(proposal['app_version_id'])

  return workspaceId && appId && proposalVersionId
    ? {
        scope: 'app_version',
        workspace_id: workspaceId,
        app_id: appId,
        app_version_id: proposalVersionId,
        branch: asString(where['branch_name']) || 'main',
      }
    : null
}

/** The target's head, read as a context: the version the conversation's own work is at. */
export function targetContext(target: HarnessWriteTarget): HarnessAppVersionContext {
  return {
    scope: 'app_version',
    workspace_id: target.workspace_id,
    app_id: target.app_id,
    app_version_id: target.head_version_id,
    branch: target.branch_name || 'main',
  }
}

/** The headers and body a context names: its own ids, down to its level. */
function addressOf(where: HarnessTurnContext): Omit<HarnessCallScope, 'context'> {
  if (where.scope === 'app_version') {
    return {
      workspaceId: where.workspace_id,
      appId: where.app_id,
      body: { app_version_id: where.app_version_id, branch: where.branch },
    }
  }

  return {
    workspaceId: where.scope === 'workspace' ? where.workspace_id : null,
    appId: null,
    body: null,
  }
}

/** Where the maker is: the page, else the target's head, else where they last spoke. */
function pageOf(
  target: HarnessWriteTarget | null,
  route: HarnessTurnContext | null,
  lastContext: HarnessTurnContext | null
): HarnessTurnContext | null {
  return route ?? (target ? targetContext(target) : lastContext)
}
